import { parseDateTime } from '@racemap/utilities/functions/datetime';
import { isFutureEvent, isPastEvent, isRunningEvent } from '@racemap/utilities/functions/utils';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import type { CheckpointEntry } from '../../BasicComponents/CheckList';
import { TooltipWithUTCDatetime } from '../../BasicComponents/Tooltips/TooltipWithUTCDatetime';
import { Checkpoint } from './Checkpoint';

interface Props {
  event: Immutable<CurrentEvent> | null;
}

export const StatusCheckpoint: FC<Props> = ({ event }) => {
  if (event == null) return null;

  const isPredicationEnabled = event.modules.predictive.enabled;

  if (!isPredicationEnabled) {
    return (
      <Checkpoint title="Status of prediction">
        Prediction is not activated. Reads from the timing system are not considered for race
        visualization.
      </Checkpoint>
    );
  }

  if (isFutureEvent(event) && event.startTime) {
    return (
      <Checkpoint title="Status of prediction">
        The event start time is set to{' '}
        <TooltipWithUTCDatetime datetime={event.startTime}>
          <strong>{parseDateTime(event.startTime).toLocaleString(DateTime.DATETIME_FULL)}</strong>
        </TooltipWithUTCDatetime>
        . With the event start time, the prediction will process reads to the live visualization of
        the race.
      </Checkpoint>
    );
  }
  if (isRunningEvent(event)) {
    return (
      <Checkpoint title="Status of prediction">
        The event is running. Until the event end time, the prediction is ongoing processing reads
        to the live visualization of the race. Monitor the timing data in real-time in the{' '}
        <Link to={`/admin/events/${event.id}/prediction/readers`}>readers-</Link> and the{' '}
        <Link to={`/admin/events/${event.id}/prediction/reads`}>reads</Link> section. You can adjust
        any information such as shadowtrack, segments, speed values, and finish times during the
        running event. Each update enforces recalc of the visualization, automatically.
      </Checkpoint>
    );
  }
  return (
    <Checkpoint title="Status of prediction">
      <p>
        The event is over. Prediction still processes new information such as new reads, correction
        of finish times, or adjustment of the shadowtrack. Each update enforces recalc of the
        visualization, automatically.
      </p>
      <br />
      {/* <p>
          In total, you have forwarded XY reads from XY readers for XY transponders.
          <br />
          XY reads have been used for processing the visualization.
        </p> */}
      The <Link to={`/admin/events/${event.id}/prediction/analyse`}>analysis</Link> section shows
      details about the timing data and the prediction.
    </Checkpoint>
  );
};

export const getStatusCheckpointStatus = (
  event: Immutable<CurrentEvent> | null,
): CheckpointEntry['status'] => {
  if (event == null) return 'secondary';

  const isPredicationEnabled = event.modules.predictive.enabled;

  if (!isPredicationEnabled && !isPastEvent(event)) {
    return 'secondary';
  }

  if (isFutureEvent(event) || isRunningEvent(event)) {
    return 'success';
  }
  return 'warning';
};
